import { SessionSupportedLanguages } from "types/session";
import { api } from "../../../config";

// This defines the main structure of the routes object. Each key defines sub routes.
interface IGetRoutes {
  ezPay: IGetRoutesEzPay;
}

/** Contract definitions * */

interface IGetRoutesEzPay {
  getConfiguration: (subDomain: string) => string;
  postValidation: (instanceId: string) => string;
  postValidationV2: (spaceKrn: string) => string;
  postBalance: () => string;
  postAccountMessages: () => string;
  getTenantSnapshot: (tenantId: string) => string;
  getPaymentSettings: (snapshotId: string) => string;
  postEmail: (email: string) => string;
  postPayableEmail: (email: string) => string;
  postEmailV2: (email: string) => string;
  postPayableSms: (phoneno: string) => string;
  postSms: (phoneno: string) => string;
  postSmsV2: (phoneno: string) => string;
  logout: () => string;
  getAccountFromSession: () => string;
  getPayableFromSession: () => string;
  updateSessionLanguage: (language: SessionSupportedLanguages) => string;
  updatePayableSessionLanguage: (language: SessionSupportedLanguages) => string;
  postPaymentMethod: () => string;
  getBarcode: () => string;
  getPayableBarcode: (payableKrn: string) => string;
  postInitiateIntent: () => string;
  postPrepareIntent: (paymentIntentId: string) => string;
  postProcessIntent: (paymentIntentId: string) => string;
  getPaymentIntent: (paymentIntentId: string) => string;
  getPayment: (paymentId: string, instanceId: string) => string;
  getPaymentV2: (paymentId: string, spaceKrn: string) => string;
  getPayableLanding: (spaceKrn: string) => string;
  getPayableBarcodeLanding: (spaceKrn: string) => string;
  patchCancelIntent: (paymentIntentId: string) => string;
  validateApplePayMerchant: () => string;
  getWalletItems: () => string;
  getWalletItemsV2: () => string;
  getPayableWalletItems: () => string;
  deleteWalletItem: (fundingSourceId: string) => string;
  deleteWalletItemV2: (fundingSourceId: string) => string;
  deletePayableWalletItem: (fundingSourceId: string) => string;
  deletePaymentDueAlerts: () => string;
  getRecentPayments: (count: number) => string;
  getUpcomingPayment: () => string;
  getPayableRecentPayments: (count: number) => string;
  getPayableUpcomingPayment: () => string;
  postCancelPayment: (paymentId: string) => string;
  postPayableCancelPayment: (paymentId: string) => string;
  postDirectLanding: (instanceId: string) => string;
  postDirectLandingV2: (spaceKrn: string) => string;
  getCsrDirectLanding: (instanceId: string, paymentIntentId: string) => string;
  getPayableCsrDirectLanding: (
    spaceKrn: string,
    paymentIntentId: string,
    payableKrn?: string
  ) => string;
  getExtendedSession: () => string;
  postLookups: (spaceKrn: string) => string;
  postPayableCreate: (typeKrn: string) => string;
  postPayableUpdate: (payableKrn: string) => string;
  postExtensionFields: (payableKrn: string) => string;
  postPayableInitiateIntent: (payableKrn: string) => string;
  postPayablePrepareIntent: () => string;
  postPayableProcessIntent: () => string;
  getRetailers: (spaceKrn: string) => string;
  postGooglePass: () => string;
  postApplePass: () => string;
  getBillerConfiguration: () => string;
  getLocationByGeo: (
    spaceKrn: string,
    latitude: string,
    longitude: string,
    radius: number,
    limit: number
  ) => string;
  getLocationByZip: (
    spaceKrn: string,
    zipcode: string,
    limit: number
  ) => string;
}

/**
 * This object defines the endpoint URLs for where requests should be sent. Note that they dont' actually send anything,
 * but instead are used by the other functions within the actions.
 * @param host
 */
const getRoutes = (
  ezpayHostInstances: string
): IGetRoutes => ({
  ezPay: {
    getConfiguration: (subDomain: string) =>
      `${ezpayHostInstances}/configurations/web?subdomain=${subDomain}`,
    postValidation: (instanceId: string) =>
      `${ezpayHostInstances}/validation/${instanceId}`,
    postValidationV2: (spaceKrn: string) =>
      `${ezpayHostInstances}/validation/v2/${spaceKrn}`,
    postBalance: () => `${ezpayHostInstances}/validation/balance`,
    getTenantSnapshot: (tenantId: string) =>
      `${ezpayHostInstances}/snapshots/tenants/${tenantId}`,
    getPaymentSettings: (snapshotId: string) =>
      `${ezpayHostInstances}/snapshots/${snapshotId}/paymentsettings`,
    postEmail: (email: string) =>
      `${ezpayHostInstances}/barcodes/email/${email}`,
    postPayableEmail: (email: string) =>
      `${ezpayHostInstances}/payables/barcodes/email/${email}`,
    postEmailV2: (email: string) =>
      `${ezpayHostInstances}/barcodes/v2/email/${email}`,
    postSms: (phoneno: string) =>
      `${ezpayHostInstances}/barcodes/sms/${phoneno}`,
    postPayableSms: (phoneno: string) =>
      `${ezpayHostInstances}/payables/barcodes/sms/${phoneno}`,
    postSmsV2: (phoneno: string) =>
      `${ezpayHostInstances}/barcodes/v2/sms/${phoneno}`,
    logout: () => `${ezpayHostInstances}/session/logout`,
    getAccountFromSession: () => `${ezpayHostInstances}/session/account`,
    updateSessionLanguage: (language: SessionSupportedLanguages) =>
      `${ezpayHostInstances}/session/language/${language}`,
    getPayableFromSession: () => `${ezpayHostInstances}/session/payable`,
    updatePayableSessionLanguage: (language: SessionSupportedLanguages) =>
      `${ezpayHostInstances}/session/payable/language/${language}`,
    postPaymentMethod: () => `${ezpayHostInstances}/payment`,
    getBarcode: () => `${ezpayHostInstances}/barcode`,
    getPayableBarcode: (payableKrn: string) => `${ezpayHostInstances}/payable/barcode/${payableKrn}`,
    postInitiateIntent: () => `${ezpayHostInstances}/initiate`,
    postPrepareIntent: (paymentIntentId: string) =>
      `${ezpayHostInstances}/prepare/${paymentIntentId}`,
    postProcessIntent: (paymentIntentId: string) =>
      `${ezpayHostInstances}/process/${paymentIntentId}`,
    getPaymentIntent: (paymentIntentId: string) =>
      `${ezpayHostInstances}/retrieve/${paymentIntentId}`,
    getPayment: (paymentId: string, instanceId: string) =>
      `${ezpayHostInstances}/validation/${instanceId}/payment/${paymentId}`,
    getPaymentV2: (paymentId: string, spaceKrn: string) =>
      `${ezpayHostInstances}/validation/v2/${spaceKrn}/payment/${paymentId}`,
    getPayableLanding: (spaceKrn: string) =>
      `${ezpayHostInstances}/validation/${spaceKrn}/payable-landing`,
    getPayableBarcodeLanding: (spaceKrn: string) =>
      `${ezpayHostInstances}/validation/${spaceKrn}/payable-barcode`,
    patchCancelIntent: (paymentIntentId: string) =>
      `${ezpayHostInstances}/cancel/${paymentIntentId}`,
    validateApplePayMerchant: () => `${ezpayHostInstances}/applepay/validate`,
    getWalletItems: () => `${ezpayHostInstances}/wallets`,
    getWalletItemsV2: () => `${ezpayHostInstances}/wallets/v2`,
    getPayableWalletItems: () => `${ezpayHostInstances}/wallets/payable`,
    deleteWalletItem: (fundingSourceId: string) =>
      `${ezpayHostInstances}/wallets/${fundingSourceId}`,
    deleteWalletItemV2: (fundingSourceId: string) =>
      `${ezpayHostInstances}/wallets/v2/${fundingSourceId}`,
    deletePayableWalletItem: (fundingSourceId: string) =>
      `${ezpayHostInstances}/wallets/payable/${fundingSourceId}`,
    deletePaymentDueAlerts: () => `${ezpayHostInstances}/alerts/unenroll`,
    getRecentPayments: (count: number) =>
      `${ezpayHostInstances}/payments/recent/${count}`,
    getUpcomingPayment: () => `${ezpayHostInstances}/payments/upcoming`,
    getPayableRecentPayments: (count: number) =>
      `${ezpayHostInstances}/payments/payable/recent/${count}`,
    getPayableUpcomingPayment: () =>
      `${ezpayHostInstances}/payments/payable/upcoming`,
    postCancelPayment: (paymentId: string) =>
      `${ezpayHostInstances}/payments/cancel/${paymentId}`,
    postPayableCancelPayment: (paymentId: string) =>
      `${ezpayHostInstances}/payments/payable/cancel/${paymentId}`,
    postDirectLanding: (instanceId: string) =>
      `${ezpayHostInstances}/validation/${instanceId}/landing`,
    postDirectLandingV2: (spaceKrn: string) =>
      `${ezpayHostInstances}/validation/v2/${spaceKrn}/landing`,
    getCsrDirectLanding: (instanceId: string, paymentIntentId: string) =>
      `${ezpayHostInstances}/validation/${instanceId}/csrDirectLanding/paymentIntent/${paymentIntentId}`,
    getPayableCsrDirectLanding: (
      spaceKrn: string,
      paymentIntentId: string,
      payableKrn?: string
    ) =>
      `${ezpayHostInstances}/validation/payable/${spaceKrn}/csrDirectLanding/paymentIntent/${paymentIntentId}${
        payableKrn ? `?payableKrn=${payableKrn}` : ""
      }`,
    getExtendedSession: () => `${ezpayHostInstances}/Session/heartbeat`,
    postAccountMessages: () => `${ezpayHostInstances}/validation/restrictions`,
    postLookups: (spaceKrn: string) =>
      `${ezpayHostInstances}/validation/${spaceKrn}/lookup`,
    postPayableCreate: (typeKrn: string) =>
      `${ezpayHostInstances}/payable/create/${typeKrn}`,
    postPayableUpdate: (payableKrn: string) =>
      `${ezpayHostInstances}/payable/update/${payableKrn}`,
    postExtensionFields: (payableKrn: string) =>
      `${ezpayHostInstances}/payable/extensionFields/${payableKrn}`,
    postPayableInitiateIntent: (payableKrn: string) =>
      `${ezpayHostInstances}/payable/initiate/${payableKrn}`,
    postPayablePrepareIntent: () => `${ezpayHostInstances}/payable/prepare`,
    postPayableProcessIntent: () => `${ezpayHostInstances}/payable/process`,
    getRetailers: (spaceKrn: string) =>
      `${ezpayHostInstances}/rcp/${spaceKrn}/retailers`,
    postGooglePass: () => `${ezpayHostInstances}/rcp/googlepass`,
    getBillerConfiguration: () => `${ezpayHostInstances}/rcp/billers/configuration`,
    postApplePass: () => `${ezpayHostInstances}/rcp/applepass`,
    getLocationByGeo: (
      spaceKrn: string,
      latitude: string,
      longitude: string,
      radius: number,
      limit: number
    ) =>
      `${ezpayHostInstances}/rcp/${spaceKrn}/locations/geo/${latitude}/${longitude}?radius=${radius}&limit=${limit}`,
    getLocationByZip: (spaceKrn: string, zipcode: string, limit: number) =>
      `${ezpayHostInstances}/rcp/${spaceKrn}/locations/zip/${zipcode}?numberOfStores=${limit}`,
  },
});

const routes = getRoutes(api.ez_pay.hostInstances);

export default routes;
